import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { AuthContext } from 'contexts/AuthContext';
import { API_BASE_URL } from 'config/api';
import { initPusher, subscribeToUserChannel, initBeams, disconnectPusher } from 'utils/pusherUtils';
import { initSocket, subscribeToSocketNotifications, disconnectSocket } from 'utils/socketUtils';
// import { useToast } from '@chakra-ui/react';

export const NotificationContext = createContext({});

// ✅ Browser Native Notification — uses OS template (Windows/Mac/Linux)
const showBrowserNotification = (title, message, type) => {
    // Not supported
    if (!('Notification' in window)) return;

    // User blocked it — cannot ask again
    if (Notification.permission === 'denied') return;

    const fire = () => {
        const notif = new Notification(title, {
            body: message,
            icon: '/logo192.png',       // ← your app logo in /public folder
            badge: '/logo192.png',      // ← small icon shown on mobile/taskbar
            tag: type,                  // same tag replaces previous notif of same type
            requireInteraction: false,  // auto dismiss after few seconds
            silent: false,              // plays default OS notification sound
        });

        // Click notification → focus app tab
        notif.onclick = () => {
            window.focus();
            notif.close();
        };
    };

    // Already granted → fire immediately
    if (Notification.permission === 'granted') {
        fire();
        return;
    }

    // Not asked yet → ask permission then fire
    if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
            if (permission === 'granted') fire();
        });
    }
};

export const NotificationProvider = ({ children }) => {
    const { auth, apiCall } = useContext(AuthContext);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [browserPermission, setBrowserPermission] = useState(
        'Notification' in window ? Notification.permission : 'unsupported'
    );

    const fetchNotifications = useCallback(async () => {
        if (!auth?.token) return;
        try {
            const data = await apiCall(`${API_BASE_URL}/api/notifications`);
            setNotifications(data.notifications || []);
            setUnreadCount(data.unreadCount || 0);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    }, [auth?.token, apiCall]);

    const markAllRead = useCallback(async () => {
        try {
            await apiCall(`${API_BASE_URL}/api/notifications/mark-read`, { method: 'PUT' });
            setUnreadCount(0);
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        } catch (error) {
            console.error('Failed to mark read:', error);
        }
    }, [apiCall]);

    const markOneRead = useCallback(async (notifId) => {
        try {
            await apiCall(`${API_BASE_URL}/api/notifications/mark-read-one/${notifId}`, { method: 'PUT' });
            setNotifications(prev =>
                prev.map(n => n._id === notifId ? { ...n, isRead: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Failed to mark single notification read:', error);
        }
    }, [apiCall]);

    // ✅ Called when user clicks "Enable Notifications" button
    const requestBrowserPermission = useCallback(async () => {
        if (!('Notification' in window)) return 'unsupported';
        if (Notification.permission === 'granted') {
            setBrowserPermission('granted');
            return 'granted';
        }
        const result = await Notification.requestPermission();
        setBrowserPermission(result);
        return result;
    }, []);

    const getBeamsToken = useCallback(async () => {
        const data = await apiCall(`${API_BASE_URL}/api/notifications/beams/auth`, { method: 'POST' });
        return data;
    }, [apiCall]);

    useEffect(() => {
        if (!auth?.id) return;

        fetchNotifications();

        // Small delay to ensure token is ready in storage
        const timer = setTimeout(() => {
            initPusher(apiCall);

            const channel = subscribeToUserChannel(auth.id, (eventName, data) => {
                // Update bell icon state
                setNotifications(prev => [
                    {
                        title: data.title,
                        message: data.message,
                        type: data.type,
                        isRead: false,
                        createdAt: new Date(),
                    },
                    ...prev.slice(0, 49),
                ]);
                setUnreadCount(prev => prev + 1);

                // ✅ Show browser native OS notification popup
                showBrowserNotification(data.title, data.message, data.type);
            });

            initBeams(auth.id, getBeamsToken).catch(err =>
                console.warn('Beams init skipped:', err.message)
            );

            initSocket(auth.id);
            // CHANGE TO: Socket.IO only logs, Pusher handles UI updates
            subscribeToSocketNotifications((eventName, data) => {
                console.log('🔄 Socket.IO backup received (Pusher handles UI):', eventName);
                // No state update here — Pusher channel above handles it
            });

            return () => {
                if (channel) channel.unbind_all();
            };
        }, 500); // Wait 500ms for token to be in storage

        return () => {
            clearTimeout(timer);
            disconnectPusher();
            disconnectSocket();
        };
    }, [auth?.id, apiCall, fetchNotifications, getBeamsToken]);

    return (
        <NotificationContext.Provider value={{
            notifications,
            unreadCount,
            fetchNotifications,
            markAllRead,
            markOneRead,
            browserPermission,
            requestBrowserPermission,   // ← expose this for the Enable button
        }}>
            {children}
        </NotificationContext.Provider>
    );
};